import { Component, h, Host, Prop, State, forceUpdate, Watch, Event, EventEmitter } from "@stencil/core";
import { i18nProvider } from "@trumpf/ux.i18n";
import loadTranslations, { TranslationKey } from "../../utils/loadTranslations";
import Fragment from "../../utils/Fragment";
import { SOFTWARE_TYPES, SoftwareType, getSoftwareModulesByType } from "../../api/software";
import { NOTIFICATION_TYPES, NotificationTypeId } from "../../api/notificationType";
import {
    APPLICATION_IDS,
    ApplicationId,
    applicationsLaser,
    applicationsOptics,
    applicationsWelding,
    TECHNOLOGIES_WITH_APPLICATION
} from "../../api/applications";
import { classifications } from "../../api/classifications";
import { SALUTATION } from "../../api/salutation";
import { CreateCaseRequest, createRequestData, PrefillData } from "../../api/createCase";
import { getCountryFromLocale, getLanguageFromLocale } from "../../utils/locale";
import { ApiClient } from "../../utils/ApiClient";
import { whenAuthenticated } from "../../utils/whenAuthenticated";
import { EnvConfig, getEnvConfig, setEnv } from "../../config/api";
import { StepBack, StepNavigation, StepNext, stepState } from "./steps";
import { ApiStore, createApiStore, loadFormDataFromApi } from "./api-store";
import { AuthStore, createAuthStore, SilentAuth } from "./auth";

const baseClass = "mf-create-service-case";

/**
 * @slot contacts-button - Slot for native contacts button to select contact data from phone address book
 * @slot connector - Slot for the connector that handles sending the form data to a backend
 */
@Component({
    tag: "mf-create-service-case",
    styleUrl: "create-service-case.css",
    shadow: false
})
export class MfCreateServiceCase {
    /**
     * If true, the form will be displayed in multiple steps
     */
    @Prop() multipleSteps: boolean = false;

    /**
     * Enable file upload in the form
     */
    @Prop() fileUpload: boolean = false;

    /**
     * data that should be prefilled in the form
     * @optional
     */
    @Prop() prefillData: PrefillData | null = null;

    /**
     * application name that will be used for identification of the source when sending the form
     */
    // @Prop() applicationName: string = "mf-service-calls";

    /**
     * The locale to use for translations and the phone input
     */
    @Prop({ reflect: true }) locale: string = "en-US";

    /**
     * Environment string to decide which endpoints to use
     */
    @Prop() env: string = "prod";

    /**
     * use onprem Public API instead of azure
     * @deprecated only used until April 2025
     */
    @Prop() legacyApi: boolean = false;

    /**
     * disable silent auth - then the application needs to supply the auth token via @trumpf/ux.utils
     * @optional
     */
    @Prop() disableSilentAuth: boolean = false;

    @State() selectedNotificationType: NotificationTypeId | null = null;
    @State() selectedSoftwareType: SoftwareType | null = null;
    @State() selectedApplication: ApplicationId | null = null;
    @State() formIsSubmitted: boolean = false;
    @State() selectedMachineTechnology: string | null = null;

    private config: EnvConfig;
    private apiClient: ApiClient;
    private apiStore: ApiStore;
    private authStore: AuthStore;
    private i18nSubscriberId: string;
    private form: HTMLFormElement;
    private callId: string = crypto.randomUUID(); // this browser API only works on localhost and over https
    private unsubscribeAuthenticated: () => void;

    connectedCallback() {
        setEnv(`${this.env}${this.legacyApi ? "Legacy" : ""}`);
        this.config = getEnvConfig();
        this.apiStore = createApiStore();
        this.authStore = createAuthStore();
        this.apiClient = new ApiClient(this.config);
        this.loadTranslations();
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
        this.unsubscribeAuthenticated = whenAuthenticated(() => this.loadFormData());
        this.apiStore.onChange("notificationTypesLoading", (notificationTypesLoading) => {
            if (!notificationTypesLoading) {
                this.selectedNotificationType = this.prefillData?.notificationType || null;
            }
        });
    }

    disconnectedCallback() {
        this.unsubscribeAuthenticated();
        i18nProvider.unsubscribe(this.i18nSubscriberId);
    }

    private loadFormData() {
        this.apiClient.requestTbdbToken().then(() => {
            loadFormDataFromApi(this.apiStore, this.apiClient);
        });
    }

    @Watch("locale")
    onLocaleChange() {
        if (this.locale !== i18nProvider.language) {
            this.loadTranslations();
        }
    }

    @Watch("prefillData")
    onPrefillDataChange() {
        if (this.prefillData) {
            const { notificationType } = this.prefillData;
            if (notificationType !== null) {
                this.selectedNotificationType = notificationType;
            }
        }
    }

    /**
     * Event that is fired when the form is submitted
     */
    @Event({
        eventName: "mfServiceCaseSubmit",
        bubbles: true,
        composed: true
    })
    serviceCaseSubmit: EventEmitter<CreateCaseRequest>;

    /**
     * Event that is fired when the form is cancelled and the form should be closed
     */
    @Event({
        eventName: "mfServiceCaseCancel",
        bubbles: true,
        composed: true
    })
    serviceCaseCancel: EventEmitter<void>;

    private loadTranslations() {
        loadTranslations(this.locale).then((locale) => {
            i18nProvider.changeLanguage(locale);
            this.locale = locale;
        });
    }

    private onSubmit(event: Event) {
        event.preventDefault();
        const { softwares, machines } = this.apiStore.state;
        const data = createRequestData({
            form: this.form,
            callId: this.callId,
            // createdWith: this.applicationName,
            softwares,
            machines
        });
        this.serviceCaseSubmit.emit(data);
        this.formIsSubmitted = true;
        if (this.config.logging) {
            // eslint-disable-next-line no-console
            console.log("Form submitted", data);
        }
    }

    private onCancel() {
        this.serviceCaseCancel.emit();
    }

    render() {
        const t = (key: TranslationKey, options: unknown = undefined) =>
            i18nProvider.t(`mf-service-calls:${String(key)}`, options as Record<string, unknown>);
        const { loading, notificationTypes, machines, softwares, softwareTypes } = this.apiStore.state;
        const { prefillData } = this;
        const showErrorNumbers =
            this.selectedNotificationType === null ||
            this.selectedNotificationType === NOTIFICATION_TYPES.FAULT_WITHOUT_STOP ||
            this.selectedNotificationType === NOTIFICATION_TYPES.MACHINE_STOP || // TODO: this should be in relation to the selected machine type
            this.selectedNotificationType === NOTIFICATION_TYPES.APPLICATION_ISSUES;
        const showApplications =
            this.selectedNotificationType === NOTIFICATION_TYPES.APPLICATION_ISSUES &&
            TECHNOLOGIES_WITH_APPLICATION.includes(this.selectedMachineTechnology as string);

        return (
            <Host>
                {!this.formIsSubmitted && (
                    <ux-form data-validation-required--msg={t("validation.requiredField")}>
                        <form onSubmit={(e) => this.onSubmit(e)} ref={(el) => (this.form = el as HTMLFormElement)}>
                            <div class="ux-u-text-align--center ux-u-margin-top-60 ux-u-margin-bottom-60">
                                <div class="ux-text-headline ux-text-headline--big">{t("headline.createCase")}</div>
                            </div>

                            {loading && <ux-loader variant={"spinner"}></ux-loader>}
                            <StepNavigation t={t} multipleSteps={this.multipleSteps} />
                            {((this.multipleSteps && stepState.step === 1) || !this.multipleSteps) && (
                                <div class="ux-row ux-u-flex-jc-center">
                                    <div class="ux-col-12 ux-col-md-10">
                                        <div class="ux-row">
                                            <div class="ux-col-12 ux-col-md-6">
                                                <div class="ux-u-margin-bottom-20">
                                                    <ux-select
                                                        required
                                                        label={t("field.reason.label")}
                                                        data-validation-required--msg={t("validation.requiredField")}
                                                        name="notificationType"
                                                        onUxChange={(event) => {
                                                            this.selectedNotificationType = Number(event.detail.value);
                                                        }}
                                                        disabled={prefillData?.notificationType}
                                                    >
                                                        <option value="" selected={!prefillData?.notificationType} disabled>
                                                            {t("select.placeholder")}
                                                        </option>
                                                        {notificationTypes.map((notificationType) => (
                                                            <option
                                                                key={String(notificationType.LabelKey)}
                                                                value={notificationType.Id}
                                                                selected={notificationType.Id === this.selectedNotificationType}
                                                            >
                                                                {t(
                                                                    `notificationType.${notificationType.LabelKey}` as TranslationKey
                                                                )}
                                                            </option>
                                                        ))}
                                                    </ux-select>
                                                </div>
                                            </div>
                                            {this.selectedNotificationType !== NOTIFICATION_TYPES.PROGRAMMING_SOFTWARE && (
                                                <div class="ux-col-12 ux-col-md-6">
                                                    <div class="ux-u-margin-bottom-20">
                                                        <ux-select
                                                            required
                                                            label={t("field.machine.label")}
                                                            data-validation-required--msg={t("validation.requiredField")}
                                                            name="productNo"
                                                            onUxChange={(event) => {
                                                                this.selectedMachineTechnology = machines.find(
                                                                    (machine) => machine.productNo === event.detail.value
                                                                )?.technology as string;
                                                            }}
                                                            disabled={prefillData?.productData.assetId}
                                                        >
                                                            <option value="" selected={!prefillData?.productData.assetId} disabled>
                                                                {t("select.placeholder")}
                                                            </option>
                                                            {machines.map((machine) => (
                                                                <option
                                                                    key={machine.productNo}
                                                                    value={machine.productNo}
                                                                    selected={
                                                                        machine.productNo === prefillData?.productData.assetId
                                                                    }
                                                                >
                                                                    {machine.nameOnLabel} ({machine.productNo})
                                                                </option>
                                                            ))}
                                                        </ux-select>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        {showApplications && (
                                            <Fragment>
                                                <div class="ux-row">
                                                    <div class="ux-col-12 ux-col-md-6">
                                                        <div class="ux-u-margin-bottom-20">
                                                            <ux-select
                                                                required
                                                                label={t("field.application.label")}
                                                                data-validation-required--msg={t("validation.requiredField")}
                                                                name="application"
                                                                onUxChange={(event) => {
                                                                    this.selectedApplication = Number(event.detail.value);
                                                                }}
                                                            >
                                                                <option value="" selected disabled>
                                                                    {t("select.placeholder")}
                                                                </option>
                                                                {applicationsLaser.map((application) => (
                                                                    <option value={application.id}>
                                                                        {t(`application.${application.labelKey}` as TranslationKey)}
                                                                    </option>
                                                                ))}
                                                            </ux-select>
                                                        </div>
                                                    </div>
                                                </div>
                                                {this.selectedApplication === APPLICATION_IDS.WELDING && (
                                                    <div class="ux-row">
                                                        <div class="ux-col-12 ux-col-md-6">
                                                            <div class="ux-u-margin-bottom-20">
                                                                <ux-select label={t("field.optics.label")} name="optics">
                                                                    <option value="" selected disabled>
                                                                        {t("select.placeholder")}
                                                                    </option>
                                                                    {applicationsOptics.map((application) => (
                                                                        <option value={application.id}>
                                                                            {t(
                                                                                `application.${application.labelKey}` as TranslationKey
                                                                            )}
                                                                        </option>
                                                                    ))}
                                                                </ux-select>
                                                            </div>
                                                        </div>
                                                        <div class="ux-col-12 ux-col-md-6">
                                                            <div class="ux-u-margin-bottom-20">
                                                                <ux-select
                                                                    label={t("field.applicationIssue.label")}
                                                                    name="applicationIssue"
                                                                >
                                                                    <option value="" selected disabled>
                                                                        {t("select.placeholder")}
                                                                    </option>
                                                                    {applicationsWelding.map((application) => (
                                                                        <option value={application.id}>
                                                                            {t(
                                                                                `application.${application.labelKey}` as TranslationKey
                                                                            )}
                                                                        </option>
                                                                    ))}
                                                                </ux-select>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </Fragment>
                                        )}
                                        {this.selectedNotificationType === NOTIFICATION_TYPES.PROGRAMMING_SOFTWARE && (
                                            <Fragment>
                                                <div class="ux-row">
                                                    <div class="ux-col-12 ux-col-md-6">
                                                        <div class="ux-u-margin-bottom-20">
                                                            <ux-select
                                                                required
                                                                label={t("field.softwareProblemClassification.label")}
                                                                data-validation-required--msg={t("validation.requiredField")}
                                                                name="softwareProblemClassification"
                                                            >
                                                                <option value="" selected disabled>
                                                                    {t("select.placeholder")}
                                                                </option>
                                                                {classifications.map((classification) => (
                                                                    <option value={classification}>
                                                                        {t(
                                                                            `softwareProblemClassification.${classification}` as TranslationKey
                                                                        )}
                                                                    </option>
                                                                ))}
                                                            </ux-select>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="ux-row">
                                                    <div class="ux-col-12 ux-col-md-6">
                                                        <div class="ux-u-margin-bottom-20">
                                                            <ux-checkbox
                                                                label={t("field.softwareInstallationProblem.label")}
                                                                name="installationProblem"
                                                                checked={prefillData?.softwareData.isInstallationProblem}
                                                                disabled={
                                                                    typeof prefillData?.softwareData.isInstallationProblem ===
                                                                    "boolean"
                                                                }
                                                            ></ux-checkbox>
                                                        </div>
                                                    </div>
                                                    <div class="ux-col-12 ux-col-md-6">
                                                        <div class="ux-u-margin-bottom-20">
                                                            <ux-checkbox
                                                                label={t("field.softwareLicenseIssue.label")}
                                                                name="licenseIssue"
                                                                checked={prefillData?.softwareData.isLicenseIssue}
                                                                disabled={
                                                                    typeof prefillData?.softwareData.isLicenseIssue === "boolean"
                                                                }
                                                            ></ux-checkbox>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="ux-row">
                                                    <div class="ux-col-12 ux-col-md-6">
                                                        <div class="ux-u-margin-bottom-20">
                                                            <ux-select
                                                                required
                                                                label={t("field.softwareType.label")}
                                                                name="softwareType"
                                                                onUxChange={(event) => {
                                                                    this.selectedSoftwareType = event.detail.value;
                                                                }}
                                                                disabled={prefillData?.softwareData.softwareType}
                                                            >
                                                                <option
                                                                    value=""
                                                                    selected={!prefillData?.softwareData.softwareType}
                                                                    disabled
                                                                >
                                                                    {t("select.placeholder")}
                                                                </option>
                                                                {softwareTypes.map((softwareType) => (
                                                                    <option
                                                                        value={softwareType}
                                                                        selected={
                                                                            prefillData?.softwareData.softwareType === softwareType
                                                                        }
                                                                    >
                                                                        {softwareType}
                                                                    </option>
                                                                ))}
                                                            </ux-select>
                                                        </div>
                                                    </div>
                                                    {(this.selectedSoftwareType === SOFTWARE_TYPES.TRUTOPS ||
                                                        this.selectedSoftwareType === SOFTWARE_TYPES.TRUTOPS_FAB ||
                                                        this.selectedSoftwareType === SOFTWARE_TYPES.TRUTOPS_BOOST) && (
                                                        <div class="ux-col-12 ux-col-md-6">
                                                            <div class="ux-u-margin-bottom-20"></div>
                                                        </div>
                                                    )}
                                                </div>
                                                <div class="ux-row">
                                                    {(this.selectedSoftwareType === SOFTWARE_TYPES.TRUTOPS ||
                                                        this.selectedSoftwareType === SOFTWARE_TYPES.TRUTOPS_FAB ||
                                                        this.selectedSoftwareType === SOFTWARE_TYPES.TRUTOPS_BOOST) && (
                                                        <div class="ux-col-12 ux-col-md-6">
                                                            <div class="ux-u-margin-bottom-20">
                                                                <ux-select
                                                                    required
                                                                    label={t("field.softwareModule.label")}
                                                                    data-validation-required--msg={t("validation.requiredField")}
                                                                    name="softwareModule"
                                                                    disabled={prefillData?.softwareData.softwareModule}
                                                                >
                                                                    <option
                                                                        value=""
                                                                        selected={!prefillData?.softwareData.softwareModule}
                                                                        disabled
                                                                    >
                                                                        {t("select.placeholder")}
                                                                    </option>
                                                                    {getSoftwareModulesByType(
                                                                        softwares,
                                                                        this.selectedSoftwareType
                                                                    ).map((softwareModule) => (
                                                                        <option
                                                                            value={softwareModule}
                                                                            selected={
                                                                                softwareModule ===
                                                                                prefillData?.softwareData.softwareModule
                                                                            }
                                                                        >
                                                                            {t(
                                                                                `softwareModule.${softwareModule}` as TranslationKey
                                                                            )}
                                                                        </option>
                                                                    ))}
                                                                </ux-select>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </Fragment>
                                        )}
                                        {showErrorNumbers && (
                                            <div class="ux-row">
                                                <div class="ux-col-12 ux-col-md-6">
                                                    <div class="ux-u-margin-bottom-20">
                                                        <label class="ux-text-tiny ux-text-tiny--bold ux-u-text-uppercase">
                                                            {t("field.errorNumber.label")}
                                                        </label>
                                                        <service-calls-dynamic-input
                                                            add-text={t("field.errorNumber.addAnotherNumber")}
                                                            input-name="errorNumber1"
                                                            prefilledValues={this.prefillData?.productData.errorNumbers || []}
                                                            init-items={this.prefillData?.productData.errorNumbers?.length || 1}
                                                            min-items="1"
                                                            max-items="6"
                                                            disabled={!!this.prefillData?.productData.errorNumbers}
                                                        ></service-calls-dynamic-input>
                                                        <div class={`${baseClass}__help-block`}>
                                                            <p class="ux-text-tiny">
                                                                {t("field.errorNumber.explanation", {
                                                                    example1: "0000FC00",
                                                                    example2: "1.10.58102409",
                                                                    example3: "6000"
                                                                })}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        <div class="ux-row">
                                            <div class="ux-col-12">
                                                <div class="ux-u-margin-bottom-20">
                                                    <ux-textarea-field
                                                        label={t("field.message.label")}
                                                        required
                                                        data-validation-required--msg={t("validation.requiredField")}
                                                        name="description"
                                                    ></ux-textarea-field>
                                                </div>
                                            </div>
                                        </div>

                                        {this.fileUpload && (
                                            <div class="ux-row">
                                                <div class="ux-col-12">
                                                    <ux-file-upload
                                                        max-file-size="2"
                                                        label={t("field.fileUpload.label")}
                                                        accepted-files="image/*,application/pdf,text/html"
                                                        max-files="4"
                                                    ></ux-file-upload>
                                                </div>
                                            </div>
                                        )}

                                        <StepNext t={t} multipleSteps={this.multipleSteps} onCancel={() => this.onCancel()} />
                                    </div>
                                </div>
                            )}

                            {((this.multipleSteps && stepState.step === 2) || !this.multipleSteps) && (
                                <div class="ux-row ux-u-flex-jc-center">
                                    <div class="fields-wrapper ux-col-12 ux-col-md-10">
                                        <div>
                                            <h2>{t("headline.contactData")}</h2>
                                            <slot name="contacts-button"></slot>
                                        </div>

                                        <div class="ux-row">
                                            <div class="ux-col-12 ux-col-md-6">
                                                <ux-select label={t("field.salutation.label")} required name="salutation">
                                                    <option
                                                        value=""
                                                        selected={typeof prefillData?.callBackContact.salutation !== "number"}
                                                        disabled
                                                    >
                                                        {t("select.placeholder")}
                                                    </option>
                                                    <option
                                                        value={SALUTATION.FEMALE}
                                                        selected={prefillData?.callBackContact.salutation === SALUTATION.FEMALE}
                                                    >
                                                        {t("field.salutation.female")}
                                                    </option>
                                                    <option
                                                        value={SALUTATION.MALE}
                                                        selected={prefillData?.callBackContact.salutation === SALUTATION.MALE}
                                                    >
                                                        {t("field.salutation.male")}
                                                    </option>
                                                </ux-select>
                                            </div>
                                        </div>

                                        <div class="ux-row">
                                            <div class="ux-col-12 ux-col-md-6">
                                                <div class="ux-u-margin-bottom-20">
                                                    <ux-input-field
                                                        data-validation-required--msg={t("validation.requiredField")}
                                                        data-validation-length--max="45"
                                                        data-validation-length--msg={t("validation.maxAllowedCharacters", {
                                                            number: 45
                                                        })}
                                                        label={t("field.firstName.label")}
                                                        name="firstName"
                                                        value={prefillData?.callBackContact.firstName}
                                                        required
                                                    ></ux-input-field>
                                                </div>
                                            </div>

                                            <div class="ux-col-12 ux-col-md-6">
                                                <div class="ux-u-margin-bottom-20">
                                                    <ux-input-field
                                                        data-validation-required--msg={t("validation.requiredField")}
                                                        data-validation-length--max="45"
                                                        data-validation-length--msg={t("validation.maxAllowedCharacters", {
                                                            number: 45
                                                        })}
                                                        label={t("field.lastName.label")}
                                                        name="lastName"
                                                        value={prefillData?.callBackContact.lastName}
                                                        required
                                                    ></ux-input-field>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="ux-row">
                                            <div class="ux-col-12 ux-col-md-6">
                                                <div class="ux-u-margin-bottom-20">
                                                    <ux-phone-input
                                                        label={t("field.phone.label")}
                                                        language={getLanguageFromLocale(this.locale)}
                                                        country={getCountryFromLocale(this.locale)}
                                                        data-validation-invalid--msg={t("validation.invalidPhoneNumber")}
                                                        data-validation-required--msg={t("validation.requiredField")}
                                                        name="phone"
                                                        value={prefillData?.callBackContact.phone}
                                                        required
                                                    />
                                                </div>
                                            </div>

                                            <div class="ux-col-12 ux-col-md-6">
                                                <div class="ux-u-margin-bottom-20">
                                                    <ux-input-field
                                                        type="email"
                                                        required
                                                        data-validation-required--msg={t("validation.requiredField")}
                                                        data-validation-length--max="100"
                                                        data-validation-length--msg={t("validation.maxAllowedCharacters", {
                                                            number: 100
                                                        })}
                                                        data-validation-invalid--msg={t("validation.invalidEmail")}
                                                        label="E-Mail"
                                                        name="email"
                                                        value={prefillData?.callBackContact.email}
                                                    ></ux-input-field>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="ux-u-margin-bottom-20">
                                            <div class="ux-text-normal ux-text-normal--light-gray">
                                                <p>{t("validation.requiredFieldsExplanation")}</p>
                                            </div>
                                        </div>

                                        <div class={`${baseClass}__button-group`}>
                                            <ux-button type="submit" variant="primary">
                                                {t("button.submit")}
                                            </ux-button>

                                            <StepBack t={t} multipleSteps={this.multipleSteps} />
                                            {!this.multipleSteps && (
                                                <ux-button type="button" onClick={() => this.onCancel()} formnovalidate>
                                                    {t("button.cancel")}
                                                </ux-button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </form>
                    </ux-form>
                )}
                <div style={{ display: this.formIsSubmitted ? "block" : "none" }}>
                    <slot name="connector" />
                </div>
                <SilentAuth
                    config={this.config}
                    disabled={this.disableSilentAuth}
                    authStore={this.authStore}
                    apiClient={this.apiClient}
                />
            </Host>
        );
    }
}
